// Framework-agnostic input validation + formatting shared by every Product 1
// form (the public web dialogs, registration and checkout, and the admin
// editors) AND by the server DTO decorators in `@munifocus/contracts-common`, so
// a value the browser accepts is a value the API accepts, and vice versa.
//
// Every check here is linear: plain character classes, no nested quantifiers.
// They run on unauthenticated input (a group booking validates up to 25
// attendees per request), and the textbook email regex backtracks
// super-linearly on hostile input.

// ── Shared result vocabulary ────────────────────────────────────────────────

/**
 * Why a value was refused. A code, not a sentence: the browser maps it to a
 * translated message with {@link validationMessageKey}, the server to an English
 * one, and neither has to parse the other's wording.
 */
export type FieldErrorCode = 'required' | 'invalid' | 'tooLong';

export interface FieldRuleOptions {
  /** Blank is an error when true; otherwise blank is valid (an optional field). */
  required?: boolean;
}

const isBlank = (value: string | null | undefined): boolean =>
  value == null || value.trim().length === 0;

// ── Email ───────────────────────────────────────────────────────────────────

/** RFC 5321 path limit, which is what a mail server will actually accept. */
export const EMAIL_MAX_LENGTH = 254;
/** RFC 5321 local-part limit. */
export const EMAIL_LOCAL_MAX_LENGTH = 64;

// RFC 5322 "atext": the characters an unquoted local part may hold. ASCII only:
// an accented local part (`andré@…`) needs SMTPUTF8 end to end, which neither our
// sender nor most municipal mail servers offer, so a message to it would bounce
// — refusing it at the form is kinder than a receipt that never arrives.
// Quoted local parts (`"jane doe"@…`) are refused for the same reason.
const LOCAL_PART = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
const DOMAIN_LABEL = /^[A-Za-z0-9-]{1,63}$/;
const TLD = /^(?:[A-Za-z]{2,63}|xn--[A-Za-z0-9-]{1,59})$/;

/** Trim and lowercase — the form every API stores and compares emails in. */
export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Is this a deliverable-looking email address? Leading/trailing whitespace is
 * ignored (callers store the {@link normalizeEmail} form).
 *
 * - exactly one `@`, no whitespace anywhere, at most 254 characters;
 * - local part: 1–64 ASCII letters, digits and `! # $ % & ' * + / = ? ^ _ \` { | } ~ . -`,
 *   with no leading, trailing or doubled dot;
 * - domain: two or more dot-separated labels of letters, digits and hyphens,
 *   none starting or ending with a hyphen; the last (TLD) is 2+ letters or a
 *   punycode `xn--` label. Internationalised domains must be typed in punycode.
 */
export function isValidEmail(value: string): boolean {
  const s = value.trim();
  if (s.length === 0 || s.length > EMAIL_MAX_LENGTH) return false;
  if (/\s/.test(s)) return false;
  const at = s.indexOf('@');
  if (at <= 0 || at !== s.lastIndexOf('@')) return false;

  const local = s.slice(0, at);
  if (local.length > EMAIL_LOCAL_MAX_LENGTH || !LOCAL_PART.test(local)) return false;
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;

  const labels = s.slice(at + 1).split('.');
  if (labels.length < 2) return false;
  for (const label of labels) {
    if (!DOMAIN_LABEL.test(label) || label.startsWith('-') || label.endsWith('-')) return false;
  }
  return TLD.test(labels[labels.length - 1]);
}

export function emailError(
  value: string | null | undefined,
  { required = true }: FieldRuleOptions = {},
): FieldErrorCode | null {
  if (isBlank(value)) return required ? 'required' : null;
  const s = (value as string).trim();
  if (s.length > EMAIL_MAX_LENGTH) return 'tooLong';
  return isValidEmail(s) ? null : 'invalid';
}

// ── Phone (North American Numbering Plan) ───────────────────────────────────

export interface PhoneRuleOptions extends FieldRuleOptions {
  /**
   * Accept a trailing extension (`x123`, `ext. 123`, `poste 123`). For business
   * lines — a municipal office, a company switchboard — not for a person's own
   * number.
   */
  allowExtension?: boolean;
}

/** Generous enough for `+1 (506) 555-0123 extension 123456`. */
export const PHONE_MAX_LENGTH = 40;

// The extension marker, then up to six digits. English and French spellings.
const EXTENSION = /^(.*?)[\s,;]*(?:extension|ext\.?|extn\.?|poste|p\.|x|#)\s*(\d{1,6})$/i;
const PHONE_CHARS = /^[\d\s().+-]+$/;

export interface ParsedPhone {
  /** The ten national digits: area code, exchange, line. */
  digits: string;
  /** Extension digits, or null. */
  extension: string | null;
}

/**
 * Parse a North American number. Accepts spaces, dots, dashes and parentheses,
 * and an optional leading `+1` / `1` country code. The area code and exchange
 * must each start with 2–9 (a NANP rule: no real number has 0 or 1 there).
 * Returns null when the value is not a valid number.
 */
export function parsePhone(value: string, { allowExtension = false } = {}): ParsedPhone | null {
  let main = value.trim();
  if (main.length === 0 || main.length > PHONE_MAX_LENGTH) return null;
  let extension: string | null = null;
  if (allowExtension) {
    const m = EXTENSION.exec(main);
    if (m) {
      main = m[1].trim();
      extension = m[2];
    }
  }
  if (!PHONE_CHARS.test(main)) return null;
  // A `+` is only ever the country-code marker, so it must lead and be `+1`.
  const plusCount = (main.match(/\+/g) ?? []).length;
  if (plusCount > 1 || (plusCount === 1 && !main.startsWith('+'))) return null;

  let digits = main.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  else if (plusCount === 1) return null; // `+` with no `1` country code after it
  if (digits.length !== 10) return null;
  if (!/[2-9]/.test(digits[0]) || !/[2-9]/.test(digits[3])) return null;
  return { digits, extension };
}

export function isValidPhone(value: string, options: { allowExtension?: boolean } = {}): boolean {
  return parsePhone(value, options) !== null;
}

/**
 * The stored form: `506-555-0123`, or `506-555-0123 ext. 12`. The same shape
 * {@link formatPhoneInput} produces, so values typed before this rule existed
 * read back unchanged. Returns the trimmed input when it does not parse (the
 * DTO has already refused it by then; this never throws).
 */
export function normalizePhone(value: string, options: { allowExtension?: boolean } = {}): string {
  const parsed = parsePhone(value, options);
  if (!parsed) return value.trim();
  const { digits, extension } = parsed;
  const base = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return extension ? `${base} ext. ${extension}` : base;
}

/**
 * {@link normalizePhone} for an optional stored field: blank → null. Parses
 * with extensions allowed — the DTO has already decided whether one was
 * acceptable, and normalising must not drop it.
 */
export function normalizeOptionalPhone(value: string | null | undefined): string | null {
  if (value == null || value.trim() === '') return null;
  return normalizePhone(value, { allowExtension: true });
}

export function phoneError(
  value: string | null | undefined,
  { required = false, allowExtension = false }: PhoneRuleOptions = {},
): FieldErrorCode | null {
  if (isBlank(value)) return required ? 'required' : null;
  return parsePhone(value as string, { allowExtension }) ? null : 'invalid';
}

function groupDigits(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  // NANP area codes never start with 1, so a leading 1 is always the country
  // code — drop it rather than letting it push the last digit off the end.
  if (digits.startsWith('1')) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length > 6) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return digits;
}

/**
 * Progressively format keystrokes into a North American number (506-269-0706),
 * capped at 10 digits so the field can't accumulate garbage. A leading `+1` /
 * `1` country code is dropped. With `allowExtension`, everything from the first
 * letter or `#` on is kept as typed (`506-555-0123 ext. 12`), so the extension
 * can be entered at all.
 */
export function formatPhoneInput(raw: string, { allowExtension = false } = {}): string {
  if (allowExtension) {
    const i = raw.search(/[A-Za-z#]/);
    if (i >= 0) {
      const tail = raw.slice(i).replace(/\s+/g, ' ').slice(0, 16);
      return `${groupDigits(raw.slice(0, i))} ${tail}`.trimStart();
    }
  }
  return groupDigits(raw);
}

// ── Canadian postal code ────────────────────────────────────────────────────

// Canada Post never uses D F I O Q U (they read as other characters to a
// sorting machine), and W and Z never lead.
const POSTAL_CODE = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

export function isValidCanadianPostalCode(value: string): boolean {
  return POSTAL_CODE.test(value.trim());
}

/** `e1c4m2` → `E1C 4M2`. Unparseable input comes back trimmed, unchanged. */
export function normalizePostalCode(value: string): string {
  const s = value.trim();
  if (!isValidCanadianPostalCode(s)) return s;
  const compact = s.replace(/[ -]/, '').toUpperCase();
  return `${compact.slice(0, 3)} ${compact.slice(3)}`;
}

export function postalCodeError(
  value: string | null | undefined,
  { required = false }: FieldRuleOptions = {},
): FieldErrorCode | null {
  if (isBlank(value)) return required ? 'required' : null;
  return isValidCanadianPostalCode(value as string) ? null : 'invalid';
}

// ── Province / territory ────────────────────────────────────────────────────

export interface CanadianProvince {
  code: string;
  en: string;
  fr: string;
}

export const CANADIAN_PROVINCES: readonly CanadianProvince[] = [
  { code: 'AB', en: 'Alberta', fr: 'Alberta' },
  { code: 'BC', en: 'British Columbia', fr: 'Colombie-Britannique' },
  { code: 'MB', en: 'Manitoba', fr: 'Manitoba' },
  { code: 'NB', en: 'New Brunswick', fr: 'Nouveau-Brunswick' },
  { code: 'NL', en: 'Newfoundland and Labrador', fr: 'Terre-Neuve-et-Labrador' },
  { code: 'NS', en: 'Nova Scotia', fr: 'Nouvelle-Écosse' },
  { code: 'NT', en: 'Northwest Territories', fr: 'Territoires du Nord-Ouest' },
  { code: 'NU', en: 'Nunavut', fr: 'Nunavut' },
  { code: 'ON', en: 'Ontario', fr: 'Ontario' },
  { code: 'PE', en: 'Prince Edward Island', fr: 'Île-du-Prince-Édouard' },
  { code: 'QC', en: 'Quebec', fr: 'Québec' },
  { code: 'SK', en: 'Saskatchewan', fr: 'Saskatchewan' },
  { code: 'YT', en: 'Yukon', fr: 'Yukon' },
];

// Case, accents, spaces, dots and hyphens all fold away, so `N.-B.`, `nb`,
// `Nouveau Brunswick` and `nouveau-brunswick` meet at one key.
const foldProvince = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[\s.'’-]+/g, '');

/** Spellings people still type: older postal abbreviations and initialisms. */
const PROVINCE_ALIASES: Record<string, string> = {
  pei: 'PE',
  ipe: 'PE',
  nwt: 'NT',
  tno: 'NT',
  nfld: 'NL',
  nf: 'NL',
  tnl: 'NL',
  pq: 'QC',
  yukonterritory: 'YT',
  yk: 'YT',
};

const PROVINCE_LOOKUP = new Map<string, CanadianProvince>([
  ...CANADIAN_PROVINCES.flatMap((p) =>
    [p.code, p.en, p.fr].map((name): [string, CanadianProvince] => [foldProvince(name), p]),
  ),
  ...Object.entries(PROVINCE_ALIASES).map(([alias, code]): [string, CanadianProvince] => [
    alias,
    CANADIAN_PROVINCES.find((p) => p.code === code) as CanadianProvince,
  ]),
]);

/**
 * Find a province or territory by its two-letter code or its English or French
 * name — case, accents and hyphens ignored. Registries store the English name
 * (`New Brunswick`), other places the code, so both are accepted.
 */
export function findCanadianProvince(value: string): CanadianProvince | null {
  return PROVINCE_LOOKUP.get(foldProvince(value)) ?? null;
}

export function isValidCanadianProvince(value: string): boolean {
  return findCanadianProvince(value) !== null;
}

/**
 * The stored spelling: the English name (`New Brunswick`), which is what the
 * registries hold and filter on. Unknown input comes back trimmed, unchanged.
 */
export function normalizeProvince(value: string): string {
  return findCanadianProvince(value)?.en ?? value.trim();
}

export function provinceError(
  value: string | null | undefined,
  { required = false }: FieldRuleOptions = {},
): FieldErrorCode | null {
  if (isBlank(value)) return required ? 'required' : null;
  return isValidCanadianProvince(value as string) ? null : 'invalid';
}

// ── Street address and city ─────────────────────────────────────────────────

export const STREET_ADDRESS_MAX_LENGTH = 200;
export const CITY_MAX_LENGTH = 100;

/**
 * A mailing line needs a civic number, PO box or rural route number *and* a
 * street or box word: at least one letter and one digit. `123 Main Street`,
 * `PO Box 45`, `RR 2, Site 4` and `66 chemin Hierlihy` all pass; `Main Street`
 * and `12345` do not. Letters include accented ones.
 */
export function isValidStreetAddress(value: string): boolean {
  const s = value.trim();
  return s.length > 0 && /\p{L}/u.test(s) && /\d/.test(s);
}

export function streetAddressError(
  value: string | null | undefined,
  {
    required = false,
    maxLength = STREET_ADDRESS_MAX_LENGTH,
  }: FieldRuleOptions & { maxLength?: number } = {},
): FieldErrorCode | null {
  if (isBlank(value)) return required ? 'required' : null;
  const s = (value as string).trim();
  if (s.length > maxLength) return 'tooLong';
  return isValidStreetAddress(s) ? null : 'invalid';
}

/** A city or town: letters required, no digits-only junk. */
export function cityError(
  value: string | null | undefined,
  { required = false, maxLength = CITY_MAX_LENGTH }: FieldRuleOptions & { maxLength?: number } = {},
): FieldErrorCode | null {
  if (isBlank(value)) return required ? 'required' : null;
  const s = (value as string).trim();
  if (s.length > maxLength) return 'tooLong';
  return /\p{L}/u.test(s) ? null : 'invalid';
}

// ── Messages ────────────────────────────────────────────────────────────────

export type ValidatedField =
  'email' | 'phone' | 'phoneExt' | 'postalCode' | 'province' | 'streetAddress' | 'city';

const INVALID_KEY: Record<ValidatedField, string> = {
  email: 'common.validation.emailInvalid',
  phone: 'common.validation.phoneInvalid',
  phoneExt: 'common.validation.phoneExtInvalid',
  postalCode: 'common.validation.postalCodeInvalid',
  province: 'common.validation.provinceInvalid',
  streetAddress: 'common.validation.streetAddressInvalid',
  city: 'common.validation.cityInvalid',
};

/**
 * The i18n key (in `libs/i18n`, both locales) for a refusal, so every form
 * words the same problem the same way. `tooLong` takes a `{{count}}`.
 */
export function validationMessageKey(field: ValidatedField, code: FieldErrorCode): string {
  if (code === 'required') return 'common.validation.required';
  if (code === 'tooLong') return 'common.validation.tooLong';
  return INVALID_KEY[field];
}
